import { Component, OnInit, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import * as Chart from 'chart.js';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { FarmSiteService } from 'src/app/core/services/farm-site.service';
import CustomerPondModel from '../../../core/models/customer-pond.model';
import { CustomerPondService } from '../../../core/services/customer-pond.service';
import FarmSiteModel from 'src/app/core/models/farm-site.model';
import PreferencesModel from 'src/app/core/models/preferences.model';
import { PreferencesService } from 'src/app/core/services/preferences.service';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';

declare var $: any;

/**
 * @component PondComponent
 * @selector app-pond
 * @description
 * The `PondComponent` is responsible for displaying comprehensive metrics related to ponds within the Farm Metrics dashboard.
 * It visualizes data regarding Active Ponds, Inactive Ponds, and Pond Size distribution across the farm(s).
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches and aggregates data for active ponds, inactive ponds, and pond sizes from various farm sites.
 * 2. **Visualization**: Renders three distinct charts (Active Ponds, Inactive Ponds, Pond Size) using Chart.js. Supports toggling between Doughnut and Bar charts.
 * 3. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Provider, Employee).
 * 4. **User Preferences**: Loads and saves user preferences for chart types for each metric category.
 * 5. **Detailed Reporting**: Displays tabular lists of active and inactive ponds with search functionality.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: View metrics for their specific farm business.
 * - **Farm Manager**: View metrics for farms assigned to them.
 * - **Business Admin (Provider)**: View metrics for farms associated with the provider.
 * - **Provider Manager**: View metrics for farms assigned to their technicians.
 * - **Employee**: View metrics for all farm sites (internal admin).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessName`: Business name.
 * - `loginBusinessCategory`: Business category.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `activepondType`: Saved preference for Active Pond chart type.
 * - `inactivepondType`: Saved preference for Inactive Pond chart type.
 * - `pondsizeType`: Saved preference for Pond Size chart type.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Identifies user context from `localStorage`.
 *    - Fetches relevant farm sites based on role.
 *    - Calls `getOnLoadCallsData` with farm site IDs.
 * 2. **Data Fetching (`getOnLoadCallsData`)**:
 *    - Fetches summaries for Active Ponds, Inactive Ponds, and Pond Sizes concurrently.
 *    - Fetches detailed lists for Active and Inactive ponds.
 *    - Prepares chart data (`piekeys`, `piedata`, etc.).
 *    - Renders charts (`prepareActivePondChart`, `prepareinActivePondChart`, `preparePondSizeChart`).
 * 3. **Interaction**:
 *    - User toggles chart types via selection methods (e.g., `activepondgraphselection`).
 *    - User searches lists via search methods (e.g., `searchActiveBusiness`).
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-pond',
  templateUrl: './pond.component.html',
  styleUrls: ['./pond.component.scss']
})
export class PondComponent implements OnInit, OnDestroy {
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PreferencesModel>} User preferences for chart display settings. */
  public preferences: Array<PreferencesModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds (used for summary data). */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<CustomerPondModel>} List of active pond locations (summary). */
  public activePondLocations: Array<CustomerPondModel>;

  /** @type {Array<CustomerPondModel>} List of inactive pond locations (summary). */
  public inActivePondLocations: Array<CustomerPondModel>;

  /** @type {Array<CustomerPondModel>} List of pond size locations (summary). */
  public pondSizeLocations: Array<CustomerPondModel>;

  /** @type {Array<CustomerPondModel>} Detailed list of active ponds. */
  public activeandsizeList: Array<CustomerPondModel>;

  /** @type {Array<CustomerPondModel>} Detailed list of inactive ponds. */
  public inactiveandsizeList: Array<CustomerPondModel>;

  /** @type {Array<PersonsModel>} List of technicians (for Provider Manager role). */
  public technicians: Array<PersonsModel>;

  /** @type {boolean} Flag indicating if the page data has loaded. */
  public pgLoad = false;

  /** @type {string} Title for the Active Pond chart. */
  public charttitle: string;

  /** @type {string} Title for the Inactive Pond chart. */
  public inActivecharttitle: string;

  /** @type {string} Title for the Pond Size chart. */
  public pondSizecharttitle: string;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {Array<any>} List of farm site IDs used for data fetching. */
  public farmSiteIds = new Array();

  /** @type {Array<any>} List of technician IDs (for Provider Manager). */
  public technicianIds = new Array();

  /*doc charts declaration*/
  /** @type {any} Configuration object for the Active Pond chart. */
  public activepondgraphconfig: any;

  /** @type {any} Chart.js instance for the Active Pond chart. */
  public activepondgraph: any;

  /** @type {string} Current chart type for Inactive Ponds (e.g., 'bar', 'doughnut'). */
  public inactivepondType: string;

  /** @type {any} Configuration object for the Inactive Pond chart. */
  public inactivepondgraphconfig: any;

  /** @type {any} Chart.js instance for the Inactive Pond chart. */
  public inactivepondgraph: any;
  /*end inactivepond charts declaration*/

  /*pondsize charts declaration*/
  /** @type {string} Current chart type for Pond Size (e.g., 'bar', 'doughnut'). */
  public pondsizeType: string;

  /** @type {any} Configuration object for the Pond Size chart. */
  public pondsizegraphconfig: any;

  /** @type {any} Chart.js instance for the Pond Size chart. */
  public pondsizegraph: any;
  /*end pondsize charts declaration*/

  /** @type {string} Default chart type if preference not found. */
  public chartType: string;

  /** @type {boolean} Flag indicating if the initial response has loaded. */
  public responseLoad: boolean;

  // activeandsizeList: any;
  /** @type {string} Search keyword for Active Ponds list. */
  public activesearch: string;

  /** @type {string} Search keyword for Inactive Ponds list. */
  public inactivesearch: string;

  /** @type {string} Search keyword for Pond Size list. */
  public sizesearch: string;

  // inactiveandsizeList: any;
  /** @type {Array<string>} Keys (labels) for the Active Pond chart. */
  public piekeys: Array<string>;

  /** @type {Array<CustomerPondModel>} Data values for the Active Pond chart. */
  public piedata: Array<CustomerPondModel>;

  /** @type {string[]} Keys (labels) for the Inactive Pond chart. */
  public inActivepiekeys: string[];

  /** @type {Array<CustomerPondModel>} Data values for the Inactive Pond chart. */
  public inActivepiedata: Array<CustomerPondModel>;

  /** @type {string[]} Keys (labels) for the Pond Size chart. */
  public pondSizepiekeys: string[];

  /** @type {Array<CustomerPondModel>} Data values for the Pond Size chart. */
  public pondSizepiedata: Array<CustomerPondModel>;

  /** @type {string} Current chart type for Active Ponds (e.g., 'bar', 'doughnut'). */
  public activepondType: string;

  /** @type {boolean} Flag to clear search input for active ponds. */
  public activeclearSearchInput: boolean;

  /**
   * @constructor
   * @description Initializes the component and services.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {PersonsService} personService - Service for person-related operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {PreferencesService} preferencesService - Service for managing user preferences.
   * @param {CustomerPondService} customerPondService - Service for fetching pond data.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(public sidenavaction: Sidenavaction, public personService: PersonsService, public farmSiteService: FarmSiteService, public preferencesService: PreferencesService, public customerPondService: CustomerPondService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description
   * Lifecycle hook that initializes the component.
   * 1. Sets the previous route.
   * 2. Retrieves user context from localStorage.
   * 3. Determines the chart type preferences for Active, Inactive, and Pond Size charts.
   * 4. Fetches farm sites based on the user's role and business category.
   * 5. Calls `getOnLoadCallsData` to fetch metrics data.
   *
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.farmSiteIds = new Array();
      this.activeclearSearchInput = false;
      if (this.loginPersonType != 'Business Admin') {
        this.activepondType = localStorage.getItem('activepondType');
        this.inactivepondType = localStorage.getItem('inactivepondType');
        this.pondsizeType = localStorage.getItem('pondsizeType');
        if (this.activepondType == '') {
          this.chartType = 'bar';
        }
        if (this.inactivepondType == '') {
          this.chartType = 'bar';
        }
        if (this.pondsizeType == '') {
          this.chartType = 'bar';
        }
      }
      // get person list based on login businessid
      if (this.loginPersonType == 'Business Admin') {
        this.farmSites = await this.farmSiteService.getFarmeSitesByBAdminId(localStorage.businessID);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      } else {
        this.technicians = await this.personService.getAllTechniciansByProviderManagerId(localStorage.personID);
        this.technicianIds = this.technicians.map(technician => technician.id);
        this.farmSites = await this.farmSiteService.getFarmeSitesByTechnicianIDs(this.technicianIds);
        this.farmSiteIds = this.farmSites.map(farmsite => farmsite.id);
        this.getOnLoadCallsData(this.farmSiteIds);
      }
      this.responseLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * Cancels ongoing HTTP requests for FarmSite, CustomerPond, and Preferences services.
   *
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.farmSiteService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.preferencesService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getChattypeinDB
   * @async
   * @description
   * Fetches user preferences for chart types from the database.
   * Updates the chart type variables and re-renders charts if preferences exist.
   * @returns {Promise<void>}
   */
  public async getChattypeinDB() {
    try {
      let preferences = await this.preferencesService.getPeferencesbyUserIDBusinessID(localStorage.businessID, localStorage.userID);
      if (preferences.length > 0) {
        this.chartType = preferences[0].chartType;
      } else {
        this.chartType = 'doughnut';
      }
      this.prepareActivePondChart(this.chartType);
      this.prepareinActivePondChart(this.chartType);
      this.preparePondSizeChart(this.chartType);
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getOnLoadCallsData
   * @async
   * @description
   * Fetches pond metrics data for the provided farm sites.
   * 1. Calls `getActivePondSummary`, `getInActivePondSummary`, `getPondSizeSummary`, `getInActivePondDetails`, and `getActivePondDetails` concurrently.
   * 2. Processes summary data for Active, Inactive, and Pond Size charts.
   * 3. Loads user preferences for chart type if applicable.
   * 4. Renders the charts.
   *
   * @param {Array<any>} farmSiteIds - List of farm site IDs to fetch data for.
   * @returns {Promise<void>}
   */
  public async getOnLoadCallsData(farmSiteIds) {
    try {
      let promiseResults = new Array();
      await Promise.all([
        await this.customerPondService.getActivePondSummary(farmSiteIds),
        await this.customerPondService.getInActivePondSummary(farmSiteIds),
        await this.customerPondService.getPondSizeSummary(farmSiteIds),
        await this.customerPondService.getInActivePondDetails(farmSiteIds),
        await this.customerPondService.getActivePondDetails(farmSiteIds)
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        alert(reject);
      });
      this.customerPonds = promiseResults[0];
      if (this.customerPonds !== undefined) {
        let chartdataobj = Object.assign({}, this.customerPonds);
        delete chartdataobj['pondActiveCount'];
        this.piekeys = Object.keys(chartdataobj);
        this.piedata = Object.values(chartdataobj);
        this.pgLoad = true;
        if (this.loginPersonType == 'Business Admin') {
          this.getChattypeinDB();
        } else {
          let activepondcharttype = this.activepondType || 'doughnut';
          this.prepareActivePondChart(activepondcharttype);
        }
        this.customerPonds = promiseResults[1];
        if (this.customerPonds !== undefined) {
          let chartdataobj1 = Object.assign({}, this.customerPonds);
          delete chartdataobj1['pondInActiveCount'];
          this.inActivepiekeys = Object.keys(chartdataobj1);
          this.inActivepiedata = Object.values(chartdataobj1);
          this.pgLoad = true;
          if (this.loginPersonType == 'Business Admin') {
            this.getChattypeinDB();
          } else {
            let inactivepondcharttype = this.inactivepondType || 'doughnut';
            this.prepareinActivePondChart(inactivepondcharttype);
          }
        }
        this.customerPonds = promiseResults[2];
        if (this.customerPonds !== undefined) {
          let chartdataobj2 = Object.assign({}, this.customerPonds);
          delete chartdataobj2['pondsCount'];
          this.pondSizepiekeys = Object.keys(chartdataobj2);
          this.pondSizepiedata = Object.values(chartdataobj2);
          if (this.loginPersonType == 'Business Admin') {
            this.getChattypeinDB();
          } else {
            let pondsizecharttype = this.pondsizeType || 'doughnut';
            this.preparePondSizeChart(pondsizecharttype);
          }
        }
        this.inactiveandsizeList = promiseResults[3];
        this.activeandsizeList = promiseResults[4];
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method prepareActivePondChart
   * @description
   * Configures and renders the Active Pond chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `activepondtype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} activepondtype - The type of chart to render ('doughnut' or 'bar').
   * @returns {void}
   */
  public prepareActivePondChart(activepondtype: string) {
    this.charttitle = 'Ponds by period of activity' + ' ' + ' ' + ' ';
    if (this.customerPonds['pondActiveCount']) {
      this.charttitle += this.customerPonds['pondActiveCount'] + ' ponds';
    }
    let ctx: any = document.getElementById('activePondChart');
    let ctx1: any = ctx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.piedata, function (o) {
      return o != 0;
    });
    // if all are not zeros
    let activepondnoData = false;
    if (isallareZeros.length == 0) {
      activepondnoData = true;
    }
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (activepondtype == 'bar') {
      legenddisply = false;
    }
    this.activepondgraphconfig = {
      // type: 'doughnut',
      data: {
        labels: this.piekeys,
        datasets: [{
          backgroundColor: [
            '#F0785F',
            '#3498db',
            '#FAFF75',
            '#9b59b6',
            '#6AD276'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.piedata
        }]
      },
      options: {
        legend: {
          // display: activepondnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: activepondnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.activepondgraphconfig);
    temp.type = activepondtype || 'doughnut';
    // FOR NO DATA FOUND
    this.activepondgraph = new Chart(ctx1, temp);
  }

  /**
   * @method activepondgraphselection
   * @description
   * Handles the user's selection of chart type for Active Ponds (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} activepondtype - The selected chart type.
   * @returns {void}
   */
  public activepondgraphselection(activepondtype: string) {
    if (activepondtype == 'bar') {
      let ele = '<canvas id=\'activePondChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('activePondChart');
      let ctx1: any = ctx.getContext('2d');
      if (this.activepondgraph) {
        this.activepondgraph.destroy();
      }
      this.activepondgraphconfig.options.legend.display = false; //check
      let temp = $.extend(true, {}, this.activepondgraphconfig);
      temp.type = 'bar';
      this.activepondgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
        this.pgLoad = true;
      } else {
        localStorage.setItem('activepondType', temp.type);
      }
    } else if (activepondtype == 'doughnut') {
      let ele = '<canvas id=\'activePondChart\'></canvas>';
      $('#canvasarea').html(ele);
      let ctx: any = document.getElementById('activePondChart');
      let ctx1: any = ctx.getContext('2d');
      if (this.activepondgraph) {
        this.activepondgraph.destroy();
      }
      this.activepondgraphconfig.options.legend.display = true; //check
      let temp = $.extend(true, {}, this.activepondgraphconfig);
      temp.type = 'doughnut';
      this.activepondgraph = new Chart(ctx1, temp);
      if (this.loginPersonType == 'Business Admin') {
        this.charttypesavetoDB(temp.type);
        this.pgLoad = true;
      } else {
        localStorage.setItem('activepondType', temp.type);
      }
    }
  }

  /**
   * @method prepareinActivePondChart
   * @description
   * Configures and renders the Inactive Pond chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `inactivepondtype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} inactivepondtype - The type of chart to render ('doughnut' or 'bar').
   * @returns {void}
   */
  public prepareinActivePondChart(inactivepondtype: string) {
    this.inActivecharttitle = 'Ponds by period of InActivity' + ' ' + ' ' + ' ';
    if (this.customerPonds['pondInActiveCount']) {
      this.inActivecharttitle += this.customerPonds['pondInActiveCount'] + ' ponds';
    }
    let inActivectx: any = document.getElementById('inactivePondChart');
    let inActivectx1: any = inActivectx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.inActivepiedata, function (o) {
      return o != 0;
    }); // if all are not zeros
    let inactivepondnoData = false;
    if (isallareZeros.length == 0) {
      inactivepondnoData = true;
    }
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (inactivepondtype == 'bar') {
      legenddisply = false;
    }
    this.inactivepondgraphconfig = {
      // type: 'doughnut',
      data: {
        labels: this.inActivepiekeys,
        datasets: [{
          backgroundColor: [
            '#F0785F',
            '#3498db',
            '#FAFF75',
            '#9b59b6',
            '#6AD276'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.inActivepiedata
        }]
      },
      options: {
        legend: {
          // display: inactivepondnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: inactivepondnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.inactivepondgraphconfig);
    temp.type = inactivepondtype || 'doughnut';
    // FOR NO DATA FOUND
    this.inactivepondgraph = new Chart(inActivectx1, temp);
  }

  /**
   * @method inactivepondgraphselection
   * @description
   * Handles the user's selection of chart type for Inactive Ponds (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} inactivepondtype - The selected chart type.
   * @returns {void}
   */
  public inactivepondgraphselection(inactivepondtype: string) {
    try {
      if (inactivepondtype == 'bar') {
        let ele = '<canvas id=\'inactivePondChart\'></canvas>';
        $('#inactivePondcanvasarea').html(ele);
        let inActivectx: any = document.getElementById('inactivePondChart');
        let inActivectx1: any = inActivectx.getContext('2d');
        if (this.inactivepondgraph) {
          this.inactivepondgraph.destroy();
        }
        this.inactivepondgraphconfig.options.legend.display = false; //check
        let temp = $.extend(true, {}, this.inactivepondgraphconfig);
        temp.type = 'bar';
        this.inactivepondgraph = new Chart(inActivectx1, temp);
        if (this.loginPersonType == 'Business Admin') {
          this.charttypesavetoDB(temp.type);
          this.pgLoad = true;
        } else {
          localStorage.setItem('inactivepondType', temp.type);
        }
      } else if (inactivepondtype == 'doughnut') {
        let ele = '<canvas id=\'inactivePondChart\'></canvas>';
        $('#inactivePondcanvasarea').html(ele);
        let inActivectx: any = document.getElementById('inactivePondChart');
        let inActivectx1: any = inActivectx.getContext('2d');

        if (this.inactivepondgraph) {
          this.inactivepondgraph.destroy();
        }
        this.inactivepondgraphconfig.options.legend.display = true; //check
        let temp = $.extend(true, {}, this.inactivepondgraphconfig);
        temp.type = 'doughnut';
        this.inactivepondgraph = new Chart(inActivectx1, temp);
        if (this.loginPersonType == 'Business Admin') {
          this.charttypesavetoDB(temp.type);
          this.pgLoad = true;
        } else {
          localStorage.setItem('inactivepondType', temp.type);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method preparePondSizeChart
   * @description
   * Configures and renders the Pond Size chart using Chart.js.
   * Supports both Doughnut and Bar chart types based on the `pondsizetype` parameter.
   * Handles "No Data" scenarios.
   *
   * @param {string} pondsizetype - The type of chart to render ('doughnut' or 'bar').
   * @returns {void}
   */
  public preparePondSizeChart(pondsizetype: string) {
    this.pondSizecharttitle = 'Ponds by pond size' + ' ' + ' ' + ' ';
    if (this.customerPonds['pondsCount']) {
      this.pondSizecharttitle += this.customerPonds['pondsCount'] + ' ponds';
    }
    let pondSizectx: any = document.getElementById('pondSizeChart');
    let pondSizectx1: any = pondSizectx.getContext('2d');
    // FOR NO DATA FOUND
    let isallareZeros = _.filter(this.pondSizepiedata, function (o) {
      return o != 0;
    });
    // if all are not zeros
    let pondsizepondnoData = false;
    if (isallareZeros.length == 0) {
      pondsizepondnoData = true;
    }
    // FOR NO DATA FOUND
    let legenddisply = true;
    if (pondsizetype == 'bar') {
      legenddisply = false;
    }
    this.pondsizegraphconfig = {
      data: {
        labels: this.pondSizepiekeys,
        datasets: [{
          backgroundColor: [
            '#F0785F',
            '#3498db',
            '#FAFF75',
            '#9b59b6',
            '#6AD276'
          ],
          // data: [12, 19, 3, 17, 28, 24, 7]
          data: this.pondSizepiedata
        }]
      },
      options: {
        legend: {
          // display: pondsizepondnoData ? false : true,
          display: legenddisply,
          labels: {
            fontColor: 'gray',
          },
          position: 'right',
        },
        title: {
          display: true,
          text: pondsizepondnoData ? 'No Data found' : '',
          fontSize: 15,
        },
        plugins: {
          labels: {
            render: 'value',
            fontColor: '#fff',
            fontSize: 17,
            fontStyle: 'bold',
            arc: true,
            precision: 2

          },
        },
      }
    };
    let temp = $.extend(true, {}, this.pondsizegraphconfig);
    temp.type = pondsizetype || 'doughnut';
    // FOR NO DATA FOUND
    this.pondsizegraph = new Chart(pondSizectx1, temp);
  }

  /**
   * @method pondsizegraphselection
   * @description
   * Handles the user's selection of chart type for Pond Size (Bar or Doughnut).
   * Destroys the existing chart and creates a new one with the selected type.
   * Saves the preference to the database or localStorage.
   *
   * @param {string} pondsizetype - The selected chart type.
   * @returns {void}
   */
  public pondsizegraphselection(pondsizetype: string) {
    try {
      if (pondsizetype == 'bar') {
        let ele = '<canvas id=\'pondSizeChart\'></canvas>';
        $('#pondsizecanvasarea').html(ele);
        let pondSizectx: any = document.getElementById('pondSizeChart');
        let pondSizectx1: any = pondSizectx.getContext('2d');
        if (this.pondsizegraph) {
          this.pondsizegraph.destroy();
        }
        this.pondsizegraphconfig.options.legend.display = false; //check
        let temp = $.extend(true, {}, this.pondsizegraphconfig);
        temp.type = 'bar';
        this.pondsizegraph = new Chart(pondSizectx1, temp);
        if (this.loginPersonType == 'Business Admin') {
          this.charttypesavetoDB(temp.type);
          this.pgLoad = true;
        } else {
          localStorage.setItem('pondsizeType', temp.type);
        }
      } else if (pondsizetype == 'doughnut') {
        let ele = '<canvas id=\'pondSizeChart\'></canvas>';
        $('#pondsizecanvasarea').html(ele);
        let pondSizectx: any = document.getElementById('pondSizeChart');
        let pondSizectx1: any = pondSizectx.getContext('2d');

        if (this.pondsizegraph) {
          this.pondsizegraph.destroy();
        }
        this.pondsizegraphconfig.options.legend.display = true; //check
        let temp = $.extend(true, {}, this.pondsizegraphconfig);
        temp.type = 'doughnut';
        this.pondsizegraph = new Chart(pondSizectx1, temp);
        if (this.loginPersonType == 'Business Admin') {
          this.charttypesavetoDB(temp.type);
          this.pgLoad = true;
        } else {
          localStorage.setItem('pondsizeType', temp.type);
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllActivePondLocation
   * @async
   * @description
   * Fetches Active Pond location summary data.
   * Populates `activePondLocations`.
   * @returns {Promise<void>}
   */
  public async getAllActivePondLocation() {
    try {
      this.activePondLocations = await this.customerPondService.getActivePondLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllInActivePondLocation
   * @async
   * @description
   * Fetches Inactive Pond location summary data.
   * Populates `inActivePondLocations`.
   * @returns {Promise<void>}
   */
  public async getAllInActivePondLocation() {
    try {
      this.inActivePondLocations = await this.customerPondService.getInActivePondLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAllPondSizeLocation
   * @async
   * @description
   * Fetches Pond Size location summary data.
   * Populates `pondSizeLocations`.
   * @returns {Promise<void>}
   */
  public async getAllPondSizeLocation() {
    try {
      this.pondSizeLocations = await this.customerPondService.getPondSizeLocation(this.farmSiteIds);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method charttypesavetoDB
   * @async
   * @description
   * Saves the user's chart type preference to the database via `PreferencesService`.
   *
   * @param {string} type - The chart type to save.
   * @returns {Promise<void>}
   */
  public async charttypesavetoDB(type: string) {
    try {
      this.preferences = await this.preferencesService.addPreferences(localStorage.businessID, localStorage.userID, type);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchActiveBusiness
   * @description
   * Filters the displayed Active Ponds list based on the `activesearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunactive`.
   * @returns {void}
   */
  public searchActiveBusiness() {
    let value = this.activesearch.toLowerCase();
    $('.searchfunactive').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }

  /**
   * @method searchInActiveBusiness
   * @description
   * Filters the displayed Inactive Ponds list based on the `inactivesearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfuninactive`.
   * @returns {void}
   */
  public searchInActiveBusiness() {
    let value = this.inactivesearch.toLowerCase();
    $('.searchfuninactive').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });

  }

  /**
   * @method searchSizeBusiness
   * @description
   * Filters the displayed Pond Size list based on the `sizesearch` string.
   * Uses jQuery to toggle visibility of elements with class `searchfunsize`.
   * @returns {void}
   */
  public searchSizeBusiness() {
    let value = this.sizesearch.toLowerCase();
    $('.searchfunsize').filter(function () {
      $(this).toggle($(this).text().toLowerCase().indexOf(value) != -1);
    });
  }
}
